import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { IPatientBaseline } from "../../@types/Baselines";
import { MeasurementTypes } from "../../@types/Measurements";
import {
  colapsedSectionsPropertyNames,
  ICollapsedSectionList,
} from "../../@types/Patient";
import { IPortalPreferences } from "../../@types/Preferences";
import { AiChat } from "../../components/AiChat";
import { NewDataPopup } from "../../components/NewDataPopup";
import { queryClient } from "../../config/query";
import { VIEWED_FIRST_PATIENT } from "../../constants/localStorageKeys";
import {
  PatientPageNotificationProvider,
  usePatientPageNotification,
} from "../../contexts/PatientPageNotificationContext";
import { useMarkMeasurementCardsSeen } from "../../hooks/mutations/measurement-cards-seen";
import { useCombinedMeasurementConfigs } from "../../hooks/queries/measurement-configs";
import { usePatientInfo, usePatientWards } from "../../hooks/queries/patients";
import { useChildRoutes } from "../../hooks/useChildRoutes";
import { useToast } from "../../hooks/useToast";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { PageLayout } from "../../layout/components/PageLayout";
import { baselinesService } from "../../services/baselinesService";
import { userService } from "../../services/userService";
import { isUat } from "../../utils/environment";
import { mixpanelActions } from "../../utils/mixpanel";
import { randomIntFromInterval } from "../../utils/random";
import { scrollToSectionPatientPage } from "../../utils/scrollToSectionPatientPage";
import { safeStorage } from "../../utils/storage";
import {
  isNewAppOnboardingForPatient,
  isWardFeatureEnabledForPatient,
  resolvePatientWardIds,
} from "../../utils/wardConfig";
import { AlertsSection } from "./components/AlertsSection";
import { AppointmentsSection } from "./components/AppointmentsSection";
import { DevicesSection } from "./components/DevicesSection";
import { FormsSection } from "./components/FormsSection";
import { JournalsSection } from "./components/JournalsSection";
import { LabsSection } from "./components/LabsSection";
import { MeasurementSection } from "./components/MeasurementsSection";
import { MedicationsSection } from "./components/MedicationsSection";
import { MessagesSection } from "./components/MessagesSection";
import { NotesSection } from "./components/NotesSection";
import { PatientLateralMenuDesktop } from "./components/PatientLateralMenu";
import { PatientSubNav } from "./components/PatientSubNav";
import { PinnedEcgSection } from "./components/PinnedEcgSection";
import { PinnedSection } from "./components/PinnedSection";
import { PromsSection } from "./components/PromsSection";
import { ProtocolActionsSection } from "./components/ProtocolActionsSection";
import {
  AiChatButton,
  AiChatContainer,
  LateralMenuContainer,
  LoadingOverlay,
  PatientPageContainer,
  PatientPageContent,
  StyledSection,
} from "./PatientPage.styles";
import "react-spring-bottom-sheet/dist/style.css";

dayjs.extend(utc);

function PatientPageNewDataPopup() {
  const { hasNewData, refreshPage, dismissNewData } =
    usePatientPageNotification();

  if (!hasNewData) {
    return null;
  }

  return <NewDataPopup onRefresh={refreshPage} onDismiss={dismissNewData} />;
}

export function PatientPage() {
  const { t } = useTranslation();

  const location = useLocation();
  const { patientId } = useParams();

  const patientPageRef = useRef<null | HTMLDivElement>(null);
  const overviewSectionRef = useRef<null | HTMLDivElement>(null);
  const measurementsSectionRef = useRef<null | HTMLDivElement>(null);
  const alertsSectionRef = useRef<null | HTMLDivElement>(null);
  const promsSectionRef = useRef<null | HTMLDivElement>(null);
  const protocolActionsSectionRef = useRef<null | HTMLDivElement>(null);
  const formsSectionRef = useRef<null | HTMLDivElement>(null);
  const labsSectionRef = useRef<null | HTMLDivElement>(null);
  const notesSectionRef = useRef<null | HTMLDivElement>(null);
  const medicationSectionRef = useRef<null | HTMLDivElement>(null);
  const journalSectionRef = useRef<null | HTMLDivElement>(null);
  const messagesSectionRef = useRef<null | HTMLDivElement>(null);
  const devicesSectionRef = useRef<null | HTMLDivElement>(null);
  const pinnedSectionRef = useRef<null | HTMLDivElement>(null);
  const appointmentsSectionRef = useRef<null | HTMLDivElement>(null);
  const markedPinnedFev1SeenBaselineIdRef = useRef<string | null>(null);
  const announcedMeasurementCardsRef = useRef<Set<string>>(new Set());

  const { patient, isFetching, isRefetching } = usePatientInfo({ patientId });
  // EN-4960: the patient's full ward set drives feature flags (unioned across wards).
  const { patientWards, isPatientWardsFetched } = usePatientWards(patient?.id);

  const { hideChildRoute, showChildRoute } = useChildRoutes();

  const {
    measurementsConfigs,
    labResultsConfigs,
    measurementCardsChanged,
    measurementCardsToken,
    isFetching: isConfigsFetching,
    isLoading: isConfigsLoading,
  } = useCombinedMeasurementConfigs({
    patientId: patient?.id,
  });

  const { portalPreferences, changePortalPreferences } = useUserPreferences();
  const { showToast } = useToast();
  const { mutate: markMeasurementCardsSeen } = useMarkMeasurementCardsSeen();

  document.title = t("patientPage_documentTitle");

  const [patientCollapsedSettingsIndex, setPatientCollapsedSettingsIndex] =
    useState<number>(-1);
  const [isPatientLateralMenuOpen, setIsPatientLateralMenuOpen] =
    useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasPinnedGraph, setHasPinnedGraph] = useState(false);
  const [pinnedGraphKey, setPinnedGraphKey] = useState("123456");
  const [hasMedication, setHasMedication] = useState(true);
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [hasProtocolActions, setHasProtocolActions] = useState(false);
  const [openFev1Graph, setOpenFev1Graph] = useState(false);
  const [fev1ISHLTBaseline, setFev1ISHLTBaseline] =
    useState<IPatientBaseline>();

  const defaultCollapsedSection = {
    value: false,
    collapsedDate: "",
  };

  const [collapsedSections, setCollapsedSections] =
    useState<ICollapsedSectionList>({
      measurementSection: defaultCollapsedSection,
      labsSection: defaultCollapsedSection,
      alertsSection: defaultCollapsedSection,
      promsSection: defaultCollapsedSection,
      protocolActionsSection: defaultCollapsedSection,
      formsSection: defaultCollapsedSection,
      medicationsSection: defaultCollapsedSection,
      journalsSection: defaultCollapsedSection,
      notesSection: defaultCollapsedSection,
      messagesSection: defaultCollapsedSection,
      devicesSection: defaultCollapsedSection,
      pinnedSection: defaultCollapsedSection,
      appointmentsSection: defaultCollapsedSection,
    });

  useEffect(() => {
    const loadPreferences = async () => {
      const pfResponse = await userService.getPortalPreferences();
      changePortalPreferences(pfResponse.data);

      if (portalPreferences.collapsedSectionsConfigList) {
        const parsedPatientCollapsedSettingsList = JSON.parse(
          portalPreferences.collapsedSectionsConfigList
        );

        const patientCollapsedSettingsIndex =
          parsedPatientCollapsedSettingsList.findIndex(
            (element: {
              patientId: number;
              ICollapsedSections: ICollapsedSectionList;
            }) => element.patientId.toString() === patientId
          );

        if (patientCollapsedSettingsIndex > -1) {
          setPatientCollapsedSettingsIndex(patientCollapsedSettingsIndex);

          setCollapsedSections(
            parsedPatientCollapsedSettingsList[patientCollapsedSettingsIndex]
              .collapsedSectionsConfig
          );
        }
      }
    };

    loadPreferences();
  }, []);

  useEffect(() => {
    // Wait until the patient has loaded AND the wards query has SETTLED (success or error).
    // On error patientWards stays undefined forever (retry 1, no refetch); gating must still run
    // rather than leaving the Medication tab visible, so we proceed once settled and let
    // resolvePatientWardIds fall back to the primary ward (hospitalId).
    if (patient === undefined || !isPatientWardsFetched) return;

    // EN-4960: union across the patient's wards, falling back to the primary ward (hospitalId).
    const patientWardIds = resolvePatientWardIds(
      patientWards?.wardIds,
      patient.hospitalId
    );

    const loadMedication = () => {
      // EN-5199: gate in both directions — hidden route names persist in
      // ChildRoutesContext across patient navigation, so the menu item must be
      // explicitly restored for patients whose wards do enable medication.
      if (
        !isWardFeatureEnabledForPatient(
          patientWardIds,
          "enablePatientMedication"
        )
      ) {
        setHasMedication(false);
        hideChildRoute("Medication");
      } else {
        setHasMedication(true);
        showChildRoute("Medication");
      }
    };

    const checkForNewFev1ISHLTBaselines = async () => {
      if (
        isWardFeatureEnabledForPatient(patientWardIds, "enableISHLTBaselines")
      ) {
        const latestISHLTFev1Baseline = (
          await baselinesService.checkForNewISHLTBaselines(patient.id, "fev1")
        ).data;

        setFev1ISHLTBaseline(latestISHLTFev1Baseline);
      }
    };

    loadMedication();
    checkForNewFev1ISHLTBaselines();
  }, [patient, patientWards, isPatientWardsFetched]);

  useEffect(() => {
    // EN-5134: an admin reconfigured the measurement cards this clinician sees. Acknowledged as soon
    // as the notice is shown, not on dismiss - the toast auto-closes and may never be clicked.
    if (!patient?.id || !measurementCardsChanged || !measurementCardsToken) {
      return;
    }

    // Keyed on patient and token, not a single slot: PatientPage does not remount when :patientId
    // changes, so a plain "last token" guard would let A -> B -> A announce twice.
    const announcementKey = `${patient.id}:${measurementCardsToken}`;
    if (announcedMeasurementCardsRef.current.has(announcementKey)) {
      return;
    }

    // Claimed before the request, so a re-render or React's double-invoked effect cannot fire two
    // toasts while it is in flight; released on failure so the notice is not lost for the session
    // while the server still has the change pending.
    announcedMeasurementCardsRef.current.add(announcementKey);

    // Longer than the container's 2.5s default: this is a notice to read, not an acknowledgement of
    // something the clinician just did.
    showToast(t("patientPage_toasts_cardsUpdated"), "info", {
      autoClose: 6000,
    });

    markMeasurementCardsSeen(
      { patientId: patient.id, token: measurementCardsToken },
      {
        onError: (error) => {
          announcedMeasurementCardsRef.current.delete(announcementKey);
          console.error(t("patientPage_toasts_cardsAckFailed"), error);
        },
      }
    );
  }, [
    patient?.id,
    measurementCardsChanged,
    measurementCardsToken,
    showToast,
    markMeasurementCardsSeen,
  ]);

  useEffect(() => {
    // EN-3346: the Protocol actions table + menu item show only when the
    // patient belongs to a "NewApp" ward; a "LegacyApp"-only patient sees
    // neither. Wait until the wards query has settled before deciding.
    if (patient === undefined || !isPatientWardsFetched) {
      return;
    }

    const patientWardIds = resolvePatientWardIds(
      patientWards?.wardIds,
      patient.hospitalId
    );

    const isNewApp = isNewAppOnboardingForPatient(patientWardIds);
    setHasProtocolActions(isNewApp);

    // EN-5199: hide/show are idempotent on a Set of hidden names now, so the
    // old "re-hide on every childRoutes change" self-healing (a workaround for
    // showChildRoute rebuilding the menu from base) is no longer needed.
    if (!isNewApp) {
      hideChildRoute("Protocol actions");
    } else {
      showChildRoute("Protocol actions");
    }
  }, [patient, patientWards, isPatientWardsFetched]);

  const handleOnChangeCollapsedSections = async (
    updatedCollapsedSectionsConfig: ICollapsedSectionList
  ) => {
    let updatedCollapsedSectionsConfigList = [];

    if (portalPreferences.collapsedSectionsConfigList) {
      updatedCollapsedSectionsConfigList = JSON.parse(
        portalPreferences.collapsedSectionsConfigList
      );
      if (patientCollapsedSettingsIndex !== -1) {
        updatedCollapsedSectionsConfigList[
          patientCollapsedSettingsIndex
        ].collapsedSectionsConfig = updatedCollapsedSectionsConfig;
      } else {
        updatedCollapsedSectionsConfigList.push({
          patientId: patient.id,
          collapsedSectionsConfig: updatedCollapsedSectionsConfig,
        });

        const updatedPatientCollapsedSettingsIndex =
          updatedCollapsedSectionsConfigList.length - 1;

        setPatientCollapsedSettingsIndex(updatedPatientCollapsedSettingsIndex);
      }
    } else {
      updatedCollapsedSectionsConfigList.push({
        patientId: patient.id,
        collapsedSectionsConfig: updatedCollapsedSectionsConfig,
      });
      setPatientCollapsedSettingsIndex(0);
    }

    const parsedUpdatedCollapsedSectionsConfigList = JSON.stringify(
      updatedCollapsedSectionsConfigList
    );

    const newPortalPreferences: IPortalPreferences = {
      ...portalPreferences,
      collapsedSectionsConfigList: parsedUpdatedCollapsedSectionsConfigList,
    };
    changePortalPreferences(newPortalPreferences);
    await userService.updateCollapsedSectionsConfig(
      parsedUpdatedCollapsedSectionsConfigList
    );
  };

  const updateColapsedSections = (section: colapsedSectionsPropertyNames) => {
    const formatedSectionString =
      section.charAt(0).toUpperCase() +
      section.slice(1).replace(/([a-z])([A-Z])/g, "$1 $2");

    setCollapsedSections((prevObject) => {
      const updatedCollapsedValue = !prevObject[section]?.value;
      mixpanelActions.track(
        `UserAction: ${
          updatedCollapsedValue ? "Collapse" : "Expand"
        } ${formatedSectionString}`
      );

      const updatedCollapsedSectionsConfig = {
        ...prevObject,
        [section]: {
          value: updatedCollapsedValue,
          collapsedDate: updatedCollapsedValue ? dayjs.utc().toISOString() : "",
        },
      };

      handleOnChangeCollapsedSections(updatedCollapsedSectionsConfig);
      return updatedCollapsedSectionsConfig;
    });
  };

  useEffect(() => {
    if (isConfigsFetching) {
      return;
    }

    if (!measurementsConfigs || measurementsConfigs.length === 0) {
      hideChildRoute("Measurements");
    } else {
      showChildRoute("Measurements");
    }
  }, [isConfigsFetching, measurementsConfigs]);

  useEffect(() => {
    if (isConfigsFetching) {
      return;
    }

    if (!labResultsConfigs || labResultsConfigs.length === 0) {
      hideChildRoute("Labs");
    } else {
      showChildRoute("Labs");
    }
  }, [isConfigsFetching, labResultsConfigs]);

  useEffect(() => {
    mixpanelActions.track("Visit Screen: Patient Page");
    const viewedFirstPatient = safeStorage.getItem(VIEWED_FIRST_PATIENT);
    if (viewedFirstPatient && viewedFirstPatient === "no") {
      setIsPatientLateralMenuOpen(true);
    }
    safeStorage.setItem(VIEWED_FIRST_PATIENT, "yes");

    return () => {
      queryClient.invalidateQueries({
        queryKey: [`patient-info-${patientId}`],
      });
      queryClient.invalidateQueries({
        queryKey: [`patient-monitoring-info-${patientId}`],
      });
    };
  }, [patientId]);

  useEffect(() => {
    if (measurementsConfigs === null || labResultsConfigs === null) {
      return;
    }

    scrollToSectionPatientPage({
      overviewSectionRef,
      measurementsSectionRef,
      labsSectionRef,
      alertsSectionRef,
      promsSectionRef,
      protocolActionsSectionRef,
      formsSectionRef,
      notesSectionRef,
      medicationSectionRef,
      journalSectionRef,
      messagesSectionRef,
      devicesSectionRef,
      pinnedSectionRef,
      appointmentsSectionRef,
    });
  }, [location.pathname, measurementsConfigs, labResultsConfigs]);

  const handleOnClickToExpandLateralMenu = () => {
    setIsPatientLateralMenuOpen((prevState) => !prevState);
  };

  const handleOnEditingClick = (isEditing: boolean) => {
    setIsEditing(isEditing);
  };

  useEffect(() => {
    if (!isConfigsFetching) {
      const allConfigs = [...measurementsConfigs, ...labResultsConfigs];

      // ECG is not a measurement/lab config, so it's accepted explicitly; the
      // ECG table view is rendered in the pinned slot instead of PinnedSection.
      const isEcgPinned = portalPreferences.pinnedMeasurementType === "ecg";

      // EN-5042: the Breathlessness (mMRC) graph is hidden on UAT, so a
      // previously-pinned breathlessness card must not render either.
      const isBreathlessnessHiddenOnUat =
        isUat() && portalPreferences.pinnedMeasurementType === "dyspnea";

      const isPinnedGraphValid =
        (isEcgPinned ||
          allConfigs.some(
            (config) => config.type === portalPreferences.pinnedMeasurementType
          )) &&
        portalPreferences.pinnedMeasurementType !== undefined &&
        portalPreferences.pinnedMeasurementType !== "" &&
        !isBreathlessnessHiddenOnUat;

      if (!isPinnedGraphValid) {
        hideChildRoute("Pinned");
      } else {
        showChildRoute("Pinned");
      }

      setHasPinnedGraph(isPinnedGraphValid);
    }
  }, [
    isConfigsFetching,
    measurementsConfigs,
    labResultsConfigs,
    portalPreferences.pinnedMeasurementType,
  ]);

  useEffect(() => {
    const randomKey = randomIntFromInterval(1, 1000000).toString();

    setPinnedGraphKey(randomKey);
  }, [portalPreferences.pinnedMeasurementType]);

  useEffect(() => {
    if (
      portalPreferences.pinnedMeasurementType !== "fev1" ||
      !fev1ISHLTBaseline?.id ||
      !pinnedSectionRef.current
    ) {
      return undefined;
    }

    const baselineId = fev1ISHLTBaseline.id;
    const currentPatientId = patient.id;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          markedPinnedFev1SeenBaselineIdRef.current !== baselineId
        ) {
          markedPinnedFev1SeenBaselineIdRef.current = baselineId;
          baselinesService.markISHLTBaselineAsSeen(
            currentPatientId,
            baselineId,
            "fev1"
          );
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(pinnedSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    portalPreferences.pinnedMeasurementType,
    fev1ISHLTBaseline?.id,
    hasPinnedGraph,
    isFetching,
  ]);

  const handleFev1GraphOpen = async () => {
    if (fev1ISHLTBaseline?.id) {
      await baselinesService.markISHLTBaselineAsSeen(
        patient.id,
        fev1ISHLTBaseline.id,
        "fev1"
      );
    }
  };

  const handleOnISHLTFevChipClick = () => {
    if (portalPreferences.pinnedMeasurementType === "fev1") {
      pinnedSectionRef.current?.scrollIntoView({
        behavior: "smooth",
      });
      // Mark as seen is handled by the IntersectionObserver on pinnedSectionRef
    } else {
      setOpenFev1Graph(true);
      // Mark as seen is handled by MeasurementCard's onFev1GraphOpen callback
    }
  };

  // Show loading overlay for content, but always render PatientSubNav with its skeleton
  const isPageLoading = (isFetching && !isRefetching) || isConfigsLoading;

  return (
    <PatientPageNotificationProvider patientId={patientId}>
      <PatientPageContainer ref={patientPageRef}>
        <PatientSubNav
          isLateralPatientMenuOpenOnDesktop={isPatientLateralMenuOpen}
          ishltFev1Baseline={fev1ISHLTBaseline?.baseline}
          onISHLTFev1ChipClick={handleOnISHLTFevChipClick}
        />

        {isPageLoading || !collapsedSections ? (
          <LoadingOverlay />
        ) : (
          <>
            <PatientPageContent
              isLateralPatientMenuOpenOnDesktop={isPatientLateralMenuOpen}
            >
              <PageLayout extraPadding>
                {hasPinnedGraph &&
                portalPreferences.pinnedMeasurementType !== "" &&
                portalPreferences.pinnedMeasurementType !== undefined ? (
                  <StyledSection
                    key={pinnedGraphKey}
                    ref={pinnedSectionRef}
                    positionRelative
                  >
                    {portalPreferences.pinnedMeasurementType === "ecg" ? (
                      <PinnedEcgSection
                        collapsedSection={
                          collapsedSections.pinnedSection ??
                          defaultCollapsedSection
                        }
                        updateColapsedSections={updateColapsedSections}
                        patient={patient}
                      />
                    ) : (
                      <PinnedSection
                        collapsedSection={
                          collapsedSections.pinnedSection ??
                          defaultCollapsedSection
                        }
                        updateColapsedSections={updateColapsedSections}
                        patient={patient}
                        measurementType={
                          portalPreferences.pinnedMeasurementType as MeasurementTypes
                        }
                      />
                    )}
                  </StyledSection>
                ) : null}

                <StyledSection ref={measurementsSectionRef}>
                  <MeasurementSection
                    collapsedSection={collapsedSections.measurementSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                    configs={measurementsConfigs}
                    openFev1Graph={openFev1Graph}
                    onFev1GraphOpen={handleFev1GraphOpen}
                  />
                </StyledSection>

                <StyledSection
                  css={{ "@sm": { marginBottom: 90 } }}
                  ref={labsSectionRef}
                >
                  <LabsSection
                    collapsedSection={collapsedSections.labsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                    configs={labResultsConfigs}
                  />
                </StyledSection>

                <StyledSection ref={alertsSectionRef} positionRelative>
                  <AlertsSection
                    collapsedSection={collapsedSections.alertsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={appointmentsSectionRef} positionRelative>
                  <AppointmentsSection
                    collapsedSection={
                      collapsedSections.appointmentsSection ??
                      defaultCollapsedSection
                    }
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={promsSectionRef} positionRelative>
                  <PromsSection
                    collapsedSection={collapsedSections.promsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                {hasProtocolActions ? (
                  <StyledSection
                    ref={protocolActionsSectionRef}
                    positionRelative
                  >
                    <ProtocolActionsSection
                      collapsedSection={
                        collapsedSections.protocolActionsSection ??
                        defaultCollapsedSection
                      }
                      updateColapsedSections={updateColapsedSections}
                      patient={patient}
                    />
                  </StyledSection>
                ) : null}

                <StyledSection ref={formsSectionRef} positionRelative>
                  <FormsSection
                    collapsedSection={collapsedSections.formsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                {hasMedication ? (
                  <StyledSection ref={medicationSectionRef} positionRelative>
                    <MedicationsSection
                      collapsedSection={collapsedSections.medicationsSection}
                      updateColapsedSections={updateColapsedSections}
                      patient={patient}
                    />
                  </StyledSection>
                ) : null}

                <StyledSection ref={journalSectionRef} positionRelative>
                  <JournalsSection
                    collapsedSection={collapsedSections.journalsSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={notesSectionRef} positionRelative>
                  <NotesSection
                    collapsedSection={collapsedSections.notesSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={messagesSectionRef} positionRelative>
                  <MessagesSection
                    collapsedSection={collapsedSections.messagesSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>

                <StyledSection ref={devicesSectionRef} positionRelative>
                  <DevicesSection
                    collapsedSection={collapsedSections.devicesSection}
                    updateColapsedSections={updateColapsedSections}
                    patient={patient}
                  />
                </StyledSection>
              </PageLayout>
            </PatientPageContent>

            <LateralMenuContainer isEditing={isEditing}>
              <PatientLateralMenuDesktop
                patientId={patientId}
                open={isPatientLateralMenuOpen}
                onExpandClick={handleOnClickToExpandLateralMenu}
                onEditingClick={handleOnEditingClick}
              />
            </LateralMenuContainer>

            <PatientPageNewDataPopup />
          </>
        )}

        <AiChatContainer>
          {isAiChatOpen && (
            <AiChat
              patientId={patient?.id || 0}
              onClose={() => setIsAiChatOpen(false)}
            />
          )}
          <AiChatButton
            onClick={() => setIsAiChatOpen(!isAiChatOpen)}
            title="AI Patient Insights"
          >
            <svg
              viewBox="0 0 50 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ width: "32px", height: "32px" }}
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M43.6763 19.8047H37.1076C32.8919 19.8047 29.4606 16.2753 29.4606 12.0596V5.88312C29.4606 5.29488 28.9704 4.80469 28.3821 4.80469C27.7939 4.80469 27.3037 5.29488 27.3037 5.88312V11.9616C27.3037 17.4517 31.7155 21.9615 37.1076 21.9615H43.6763C44.2645 21.9615 44.7547 21.4714 44.7547 20.8831C44.7547 20.2949 44.2645 19.8047 43.6763 19.8047Z"
                fill="#16A8A9"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M49.072 25.2949C49.072 24.7067 48.5818 24.2165 47.9935 24.2165H46.9151H40.5426H37.1112C30.4445 24.2165 25.1504 18.7263 25.1504 11.9616V1.37335C25.0524 0.785118 24.5622 0.294922 23.9739 0.294922C23.3857 0.294922 22.8955 0.785118 22.8955 1.37335V11.9616C22.8955 19.9028 29.2681 26.4714 37.1112 26.4714H47.9935C48.5818 26.4714 49.072 25.9812 49.072 25.2949Z"
                fill="#16A8A9"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M43.6759 28.7263H37.1073C32.8916 28.7263 29.0681 27.0596 26.1269 24.4125C25.5386 23.9223 25.0484 23.4321 24.4602 23.04C24.3622 22.9419 24.1661 22.8439 23.97 22.6478C23.872 22.5498 23.6759 22.4517 23.5779 22.3537C23.0877 21.9615 22.4994 21.5694 22.0092 21.2753C21.0288 20.687 20.4406 20.491 20.4406 20.491C18.3818 19.6086 16.0288 19.1184 13.2837 19.1184H6.22492C5.63668 19.1184 5.14648 19.6086 5.14648 20.1968C5.14648 20.7851 5.63668 21.2753 6.22492 21.2753H13.3818C17.3033 21.2753 20.9308 22.7459 23.7739 25.0988H23.872C23.97 25.1968 24.0681 25.3929 24.2641 25.491C25.7347 26.9615 27.4994 28.2361 29.4602 29.1184C31.8132 30.1968 34.3622 30.7851 37.1073 30.7851H43.6759C44.2641 30.7851 44.7543 30.2949 44.7543 29.7066C44.7543 29.2164 44.2641 28.7263 43.6759 28.7263Z"
                fill="#785CB3"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M22.1075 18.6282C21.2252 16.5694 20.735 14.3145 20.735 11.9616V5.88312C20.735 5.29488 20.2448 4.80469 19.6566 4.80469C19.0683 4.80469 18.5781 5.29488 18.5781 5.88312V11.9616C18.5781 13.8243 18.8722 15.687 19.3624 17.4517C20.2448 17.7459 21.2252 18.138 22.1075 18.6282Z"
                fill="#785CB3"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M27.7935 31.1772C28.6758 33.2361 29.166 35.491 29.166 37.8439V43.9223C29.166 44.5106 29.6562 45.0008 30.2444 45.0008C30.8327 45.0008 31.3229 44.5106 31.3229 43.9223V37.8439C31.3229 35.9812 31.0288 34.1184 30.5386 32.3537C29.5582 32.0596 28.6758 31.6674 27.7935 31.1772Z"
                fill="#785CB3"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.6955 28.04H6.22492C5.63668 28.04 5.14648 28.5302 5.14648 29.1185C5.14648 29.7067 5.63668 30.1969 6.22492 30.1969H12.7935C17.0092 30.1969 20.4406 33.7263 20.4406 37.942V44.0204C20.4406 44.6087 20.9308 45.0989 21.519 45.0989C22.1073 45.0989 22.5975 44.6087 22.5975 44.0204V37.942C22.5975 32.4518 18.1857 28.04 12.6955 28.04Z"
                fill="#16A8A9"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M26.9113 38.0401C26.9113 30.0989 20.5388 23.5303 12.6956 23.5303H1.81329C1.22506 23.5303 0.734863 24.0205 0.734863 24.6087C0.734863 25.1969 1.22506 25.7852 1.81329 25.7852H2.89173H9.26427H12.6956C19.3623 25.7852 24.6564 31.2754 24.6564 38.0401V48.6283C24.6564 49.2165 25.1466 49.7067 25.7349 49.7067C26.4211 49.7067 26.9113 49.2165 26.9113 48.6283V38.0401Z"
                fill="#16A8A9"
              />
            </svg>
          </AiChatButton>
        </AiChatContainer>
      </PatientPageContainer>
    </PatientPageNotificationProvider>
  );
}
